// MARK:- Product Shop the Look. Renders every look a product belongs to beneath the
// product details, and optionally as extra product gallery slides. Impulse's gallery
// is driven from this file because assets/theme.js is frozen vendor code: the hooks
// at the bottom wrap theme.* prototypes here instead of editing the bundle.

const TPM_LOOK_SLIDE = '[data-tpm-look-slide]';

// Read the rendered page so its configured looks and Liquid availability checks
// remain the source of truth. Reuse fragments only within the short session TTL.
class TpmProductLooks extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    // Impulse builds its product sections while theme.js runs, so the instance is
    // normally ready here. The editor swaps section HTML before rebuilding the
    // instance, so retry once that section reports itself loaded.
    if (this.resolveProduct()) return;
    document.addEventListener('shopify:section:load', () => this.resolveProduct(), {
      signal: this.controller.signal
    });
  }

  disconnectedCallback() {
    this.destroy();
  }

  resolveProduct() {
    if (this.product) return true;
    const product = window.theme?.sections?.instances?.find(instance => {
      return instance.type === 'product' && instance.id === this.dataset.sectionId;
    });
    if (!product) return false;
    this.setup(product);
    return true;
  }

  setup(product) {
    this.product = product;
    this.container = this;
    const options = {signal: this.controller.signal};
    const dismissOverlay = event => {
      if (!this.activeOverlay || this.activeOverlay.panel.contains(event.target)) return;
      const trigger = event.target.closest('[data-tpm-look-zoom-hotspot]');
      if (trigger && (this.product.cache.mainSlider.contains(trigger) || this.zoomLayer?.contains(trigger))) return;

      // Dismiss on the photo without letting the same click open image zoom.
      if (this.activeOverlay.panel.parentElement.contains(event.target) ||
          (this.zoomLayer?.contains(this.activeOverlay.panel) && event.target.closest('.pswp__img, .pswp__zoom-wrap'))) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.closeOverlay(false);
    };
    ['click', 'pswpTap'].forEach(type => {
      document.addEventListener(type, dismissOverlay, {...options, capture: true});
    });
    document.addEventListener('keydown', event => {
      if (this.activeOverlay && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeOverlay();
      }
    }, {...options, capture: true});
    // MARK:- tpm:photoswipe:open comes from the wrapper at the bottom of this file.
    // It fires once PhotoSwipe has initialised, so the first slide is drawn directly
    // rather than waiting for an afterChange that has already been emitted.
    product.container.addEventListener('tpm:photoswipe:open', event => {
      const {gallery, viewer} = event.detail;
      gallery.listen('afterChange', () => this.renderZoomHotspots(gallery, viewer));
      gallery.listen('close', () => this.clearZoomHotspots());
      gallery.listen('destroy', () => this.clearZoomHotspots());
      this.renderZoomHotspots(gallery, viewer);
    }, options);
    this.adaptGallery(product);
    this.recoverZoom(product);
    this.bindLookSection(options);
    // MARK:- Wait for PDP assets before fetching; teardown cancels the pending load listener.
    if (document.readyState === 'complete') this.load();
    else window.addEventListener('load', () => this.load(), {...options, once: true});
  }

  // MARK:- Adapters for Impulse's product gallery. Each wraps a method on this one
  // instance, so assets/theme.js stays untouched and teardown restores the original.
  adaptGallery(product) {
    this.onGalleryChange = () => this.updateSlideFocus();
    this.vendor = {};

    // Every rebuild (variant switch, image set) replaces the Flickity instance and
    // drops our listener, so rebind after each one. This also covers the
    // single-image product, whose slideshow is only built once looks are appended.
    this.vendor.initProductSlider = product.initProductSlider;
    product.initProductSlider = (...args) => {
      this.vendor.initProductSlider.apply(product, args);
      this.bindGallery();
    };

    // Image sets narrow the gallery to one group; look slides belong to every group.
    if (product.imageSetArguments) {
      this.vendor.imageSetArguments = product.imageSetArguments;
      product.imageSetArguments = (...args) => {
        const setArgs = this.vendor.imageSetArguments.apply(product, args);
        if (setArgs?.cellSelector && !setArgs.cellSelector.includes(TPM_LOOK_SLIDE)) {
          setArgs.cellSelector += `, ${TPM_LOOK_SLIDE}`;
        }
        return setArgs;
      };
    }
    if (product.updateImageSetThumbs) {
      this.vendor.updateImageSetThumbs = product.updateImageSetThumbs;
      product.updateImageSetThumbs = (...args) => {
        this.vendor.updateImageSetThumbs.apply(product, args);
        product.cache.thumbSlider?.querySelectorAll('[data-tpm-look-thumb]')
          .forEach(thumb => thumb.classList.remove('hide'));
      };
    }
  }

  // Exactly one change listener on whichever Flickity instance is current.
  bindGallery() {
    const emitter = this.product.flickity?.slideshow || null;
    if (emitter !== this.galleryEmitter) {
      this.galleryEmitter?.off('change', this.onGalleryChange);
      this.galleryEmitter = emitter;
      emitter?.on('change', this.onGalleryChange);
    }
    this.updateSlideFocus();
  }

  // MARK:- Impulse builds its zoom viewer while theme.js runs, before this file is
  // evaluated, so the wrapper below never saw it and appended zoom triggers would
  // stay unbound. Build a replacement through theme.Photoswipe: the wrapped
  // initializer clears the previous namespaced bindings first, and initImageZoom is
  // deliberately not reused because it would duplicate its afterChange listener.
  recoverZoom(product) {
    const imageContainer = product.container.querySelector('[data-product-images]');
    if (!imageContainer || imageContainer.dataset.zoom === 'false') return;
    if (imageContainer.tpmPhotoswipe || !window.theme?.Photoswipe) return;
    new theme.Photoswipe(imageContainer, product.sectionId).init();
  }

  async load() {
    // Capture this load's own signal. A disconnect/reconnect installs a fresh
    // controller, so testing this.controller after the await would check the new one
    // and let a stale load — a cached source resolves immediately — mount its looks
    // alongside the new connection's.
    const {signal} = this.controller;
    // MARK:- Retain the requested source URL for actionable failure diagnostics.
    let url;
    try {
      url = new URL(this.container.dataset.sourceUrl, window.location.origin);
      const entries = await theme.tpmLookSource.get(url, {
        cacheContext: this.container.dataset.cacheContext, signal
      });
      if (signal.aborted || !this.isConnected) return;

      // Keep unrelated looks inert, including when restoring the session cache.
      const source = document.createElement('template');
      source.innerHTML = entries
        .filter(entry => entry.productIds.includes(String(this.product.productId)))
        .map(entry => entry.html).join('');
      const looks = [...source.content.children];
      if (!looks.length) return;

      const slides = [];
      const thumbs = [];
      const mediaCount = this.product.cache.mainSlider.querySelectorAll('.product-main-slide').length;

      looks.forEach((sourceLook, offset) => {
        const look = sourceLook.cloneNode(true);
        const lookId = `ProductLook-${this.product.sectionId}-${offset}`;
        look.id = lookId;
        look.classList.add('hotspots-section');
        look.querySelectorAll('script, [data-shopify-editor-block]').forEach(element => {
          if (element.tagName === 'SCRIPT') element.remove();
          else element.removeAttribute('data-shopify-editor-block');
        });

        // Editor-only unavailable markers must never become PDP hotspots.
        look.querySelectorAll('[data-button]:not([data-hotspot-product-id])').forEach(button => {
          const block = look.querySelector(`[data-hotspot-block="${button.dataset.button}"]`);
          if (block) block.remove();
          button.remove();
        });

        if (this.container.dataset.galleryEnabled === 'true') {
          const index = mediaCount + offset;
          slides.push(this.createSlide(look, lookId, index));
          thumbs.push(this.createThumbnail(look, index));
        }
        // Build gallery overlays from the original source cards, then compact
        // only the look shown below the product details.
        this.prepareLookSection(look, lookId);
        this.container.querySelector('[data-tpm-look-panels]').append(look);
      });

      this.container.hidden = false;
      if (slides.length) this.addLookMedia(slides, thumbs);
      if (window.AOS) window.AOS.refreshHard();
    } catch (error) {
      // The normal PDP stays usable if the source page is missing or unavailable.
      if (error.name !== 'AbortError') console.warn(`Unable to load Shop the Look: ${url?.href || this.container.dataset.sourceUrl}`, error);
    }
  }

  // MARK:- Gallery integration. This previously lived in assets/theme.js as
  // addShopTheLookMedia plus a theme.Slideshow.append method; it drives the same
  // Impulse objects from outside so the vendor bundle stays untouched.
  addLookMedia(slides, thumbs) {
    const product = this.product;
    const imageContainer = product.container.querySelector('[data-product-images]');
    product.cache.thumbScroller.append(...thumbs);

    if (product.flickity) this.appendLookSlides(product.flickity, slides, thumbs);
    else product.cache.mainSlider.append(...slides);

    const hasSlideshow = product.cache.mainSlider.querySelectorAll('.product-main-slide').length > 1;
    product.cache.thumbSlider.classList.toggle('medium-up--hide', !hasSlideshow);
    if (imageContainer) imageContainer.dataset.hasSlideshow = hasSlideshow;
    // A single-image product has no slideshow yet, so build it now that looks exist.
    // The wrapped initProductSlider rebinds the gallery for us on that path.
    if (!product.flickity) product.initProductSlider();
    else this.bindGallery();

    // Rebind zoom so the appended look slides open in PhotoSwipe as well.
    const zoom = imageContainer?.tpmPhotoswipe;
    if (zoom && imageContainer.dataset.zoom !== 'false') zoom.init();
  }

  appendLookSlides(flickity, slides, thumbs) {
    // An image set narrows the live cellSelector to one group, and Flickity filters
    // appended elements through it, so widen it before adding the look slides.
    const options = flickity.slideshow?.options;
    if (options?.cellSelector && !options.cellSelector.includes(TPM_LOOK_SLIDE)) {
      options.cellSelector += `, ${TPM_LOOK_SLIDE}`;
    }
    flickity.slideshow.append(slides);
    if (!flickity.args.childNav) return;

    // Refresh the thumbnail cache so childNavGoTo reaches the appended thumbs, then
    // bind only the new links; Impulse's existing listeners stay as they are.
    flickity.childNavEls = flickity.args.childNav.querySelectorAll('.product__thumb-item:not(.hide)');
    flickity.childNavLinks = flickity.args.childNav.querySelectorAll('.product__thumb-item:not(.hide) a');
    // childNavGoTo indexes this list by position, and getChildIndex reads data-index,
    // so align the two. With an image set the visible order is not the media order.
    flickity.childNavLinks.forEach((link, position) => { link.dataset.index = position; });
    const {signal} = this.controller;
    thumbs.forEach(thumb => {
      const link = thumb.querySelector('a');
      if (!link) return;
      const goToLook = event => {
        event.preventDefault();
        flickity.goToSlide(flickity.getChildIndex(link));
      };
      link.addEventListener('click', goToLook, {signal});
      link.addEventListener('focus', goToLook, {signal});
      link.addEventListener('keydown', event => {
        if (event.key === 'Enter') goToLook(event);
      }, {signal});
    });
  }

  // Hidden look slides stay out of the tab order, and any open card is dismissed.
  updateSlideFocus() {
    this.closeOverlay(false);
    this.product.cache.mainSlider.querySelectorAll('[data-tpm-look-slide]').forEach(slide => {
      slide.inert = !slide.classList.contains('is-selected');
    });
  }

  prepareLookSection(look, lookId) {
    look.querySelector('.hotspots__title')?.parentElement.remove();
    look.querySelectorAll('.hotspots__add-all, .modal--quick-shop, .quick-product__btn, .grid-product__colors, .grid-product__color-image, .grid-product__secondary-image, .tpm-stickers').forEach(element => element.remove());
    look.querySelectorAll('[data-aos]').forEach(element => element.removeAttribute('data-aos'));

    // Show every product together, without activating the source component's
    // single-card selection or quick-view initialization.
    const source = look.querySelector('hot-spots');
    const layout = document.createElement('div');
    layout.className = 'tpm-product-looks__layout';
    layout.append(...source.childNodes);
    source.replaceWith(layout);
    const photo = layout.querySelector('.hotspots');
    const image = photo.querySelector('img');
    image.sizes = '(min-width: 769px) 50vw, 100vw';
    image.alt = look.dataset.lookTitle || this.container.dataset.lookLabel;

    layout.querySelectorAll('[data-hotspot-block]').forEach((block, index) => {
      const button = layout.querySelector(`[data-button="${block.dataset.hotspotBlock}"]`);
      const card = block.querySelector('.grid__item-image-wrapper');
      if (!button || !card) return;
      const selected = button.dataset.hotspotProductId === String(this.product.productId);
      block.id = `${lookId}-Row-${index}`;
      block.classList.remove('is-active');
      block.classList.toggle('is-selected', selected);
      button.dataset.tpmLookHotspot = '';
      button.setAttribute('aria-controls', block.id);
      button.setAttribute('aria-pressed', String(selected));
      button.replaceChildren(this.plusIcon());

      const title = card.querySelector('.grid-product__title').textContent.trim();
      const price = card.querySelector('.grid-product__price');
      if (price) card.append(price);
      if (block.dataset.lookProductDetail) {
        const detail = document.createElement('div');
        detail.className = 'tpm-product-looks__detail';
        detail.textContent = block.dataset.lookProductDetail;
        card.querySelector('.grid-product__meta').append(detail);
      }
      const action = card.querySelector('.icon-container');
      if (action) {
        const isAdd = action.matches('button');
        // Retain the existing delegated cart handler and attribution metadata.
        action.className = `tpm-product-looks__action${isAdd ? ' quick-add-mobile' : ''}`;
        if (isAdd) action.type = 'button';
        const label = isAdd ? this.container.dataset.addLabel : this.container.dataset.viewProductLabel;
        action.setAttribute('aria-label', `${label}: ${title}`);
        action.title = label;
        action.replaceChildren(this.plusIcon());
        card.append(action);
      }
      card.querySelectorAll('img').forEach(image => { image.sizes = '64px'; });

      const tooltip = document.createElement('div');
      tooltip.id = `${block.id}-Tooltip`;
      tooltip.className = 'tpm-product-looks__tooltip';
      tooltip.setAttribute('role', 'tooltip');
      tooltip.hidden = true;
      const tooltipTitle = document.createElement('p');
      tooltipTitle.className = 'tpm-product-looks__tooltip-title';
      tooltipTitle.textContent = title;
      tooltip.append(tooltipTitle);
      if (price) {
        const tooltipPrice = price.cloneNode(true);
        tooltipPrice.className = 'tpm-product-looks__tooltip-price';
        tooltip.append(tooltipPrice);
      }
      button.setAttribute('aria-describedby', tooltip.id);
      photo.append(tooltip);
    });
  }

  bindLookSection(options) {
    this.container.addEventListener('click', event => {
      const button = event.target.closest('[data-tpm-look-hotspot]');
      if (!button) return;
      const look = button.closest('[data-tpm-look]');
      look.querySelectorAll('[data-hotspot-block]').forEach(block => {
        block.classList.toggle('is-selected', block.id === button.getAttribute('aria-controls'));
      });
      look.querySelectorAll('[data-tpm-look-hotspot]').forEach(pin => pin.setAttribute('aria-pressed', String(pin === button)));
      this.container.querySelector('[data-tpm-look-announcement]').textContent = button.getAttribute('aria-label');
      // Mobile feedback stays beside the pin, without scrolling past the photo.
      if (this.lookPointerType === 'touch' && this.lookTooltipButton === button) this.closeLookTooltip();
      else this.openLookTooltip(button);
    }, options);
    for (const type of ['pointerover', 'pointerout']) {
      this.container.addEventListener(type, event => {
        if (event.pointerType === 'touch') return;
        const target = event.target.closest('[data-tpm-look-hotspot], .tpm-product-looks__tooltip');
        if (!target || target.contains(event.relatedTarget)) return;
        clearTimeout(this.lookTooltipTimer);
        if (type === 'pointerover') {
          if (target.matches('[data-tpm-look-hotspot]')) this.openLookTooltip(target);
        } else {
          this.lookTooltipTimer = setTimeout(() => {
            if (!this.lookTooltipButton?.matches(':focus-visible')) this.closeLookTooltip();
          }, 180);
        }
      }, options);
    }
    this.container.addEventListener('focusin', event => {
      const button = event.target.closest('[data-tpm-look-hotspot]');
      if (button?.matches(':focus-visible')) this.openLookTooltip(button);
    }, options);
    this.container.addEventListener('focusout', event => {
      if (event.target === this.lookTooltipButton) this.closeLookTooltip();
    }, options);
    this.container.addEventListener('keydown', event => {
      this.lookPointerType = 'keyboard';
      if (event.key === 'Escape' && this.lookTooltip) {
        event.preventDefault();
        event.stopPropagation();
        this.closeLookTooltip();
      }
    }, options);
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') this.closeLookTooltip();
    }, options);
    document.addEventListener('pointerdown', event => {
      this.lookPointerType = event.pointerType;
      if (!this.lookTooltipButton?.contains(event.target) && !this.lookTooltip?.contains(event.target)) this.closeLookTooltip();
    }, options);
    window.addEventListener('resize', () => this.positionLookTooltip(), options);
  }

  openLookTooltip(button) {
    this.closeLookTooltip();
    const tooltip = document.getElementById(button.getAttribute('aria-describedby'));
    if (!tooltip) return;
    this.lookTooltipButton = button;
    this.lookTooltip = tooltip;
    button.classList.add('is-tooltip-open');
    tooltip.hidden = false;
    this.positionLookTooltip();
  }

  closeLookTooltip() {
    clearTimeout(this.lookTooltipTimer);
    this.lookTooltipButton?.classList.remove('is-tooltip-open');
    if (this.lookTooltip) this.lookTooltip.hidden = true;
    this.lookTooltipButton = null;
    this.lookTooltip = null;
  }

  positionLookTooltip() {
    if (!this.lookTooltip) return;
    const photo = this.lookTooltip.parentElement.getBoundingClientRect();
    const button = this.lookTooltipButton.getBoundingClientRect();
    const {offsetWidth: width, offsetHeight: height} = this.lookTooltip;
    const left = Math.max(12, Math.min(button.left - photo.left + (button.width - width) / 2, photo.width - width - 12));
    const below = button.bottom - photo.top + 12;
    const top = below + height <= photo.height - 12 ? below : Math.max(12, button.top - photo.top - height - 12);
    this.lookTooltip.style.left = `${left}px`;
    this.lookTooltip.style.top = `${top}px`;
  }

  plusIcon() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    for (const coordinates of [[5, 12, 19, 12], [12, 5, 12, 19]]) {
      const line = document.createElementNS(svg.namespaceURI, 'line');
      ['x1', 'y1', 'x2', 'y2'].forEach((key, index) => line.setAttribute(key, coordinates[index]));
      line.setAttribute('stroke', 'currentColor');
      line.setAttribute('stroke-width', '2');
      svg.append(line);
    }
    return svg;
  }

  createSlide(look, lookId, index) {
    const slide = document.createElement('div');
    slide.className = `product-main-slide tpm-product-looks__slide ${look.dataset.tpmLook}`;
    slide.dataset.index = index;
    slide.dataset.tpmLookSlide = '';
    slide.dataset.lookSection = look.dataset.tpmLook;
    slide.inert = true;
    look.querySelectorAll('style').forEach(style => slide.append(style.cloneNode(true)));

    const photo = look.querySelector('.hotspots').cloneNode(true);
    photo.dataset.productImageMain = '';
    photo.classList.add('product-image-main');
    photo.querySelectorAll('[data-hotspot-product-url]').forEach((button, hotspotIndex) => {
      const block = look.querySelector(`[data-hotspot-block="${button.dataset.button}"]`);
      const panel = this.createOverlay(block, button, `${lookId}-Product-${hotspotIndex}`);
      button.removeAttribute('data-button');
      this.bindHotspot(button, panel, this.controller.signal);
      photo.append(panel);
    });

    const img = photo.querySelector('img');
    img.classList.add('photoswipe__image');
    img.alt = look.dataset.lookTitle || this.container.dataset.lookLabel;
    img.sizes = '(min-width: 769px) 50vw, 100vw';
    img.dataset.photoswipeSrc = look.dataset.lookImage;
    img.dataset.photoswipeWidth = look.dataset.lookWidth;
    img.dataset.photoswipeHeight = look.dataset.lookHeight;
    img.dataset.index = index + 1;

    const zoom = this.product.cache.mainSlider.querySelector('.js-photoswipe__zoom');
    if (zoom) photo.append(zoom.cloneNode(true));
    slide.append(photo);

    const link = document.createElement('a');
    link.className = 'tpm-product-looks__slide-link';
    link.href = `#${lookId}`;
    link.textContent = look.dataset.lookTitle
      ? `${this.container.dataset.lookLabel}: ${look.dataset.lookTitle}`
      : this.container.dataset.lookLabel;
    slide.append(link);
    return slide;
  }

  createOverlay(block, button, id) {
    const panel = document.createElement('div');
    panel.id = id;
    panel.className = 'tpm-product-looks__popover';
    panel.hidden = true;
    panel.tabIndex = -1;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-labelledby', `${id}-Title`);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'tpm-product-looks__popover__close';
    close.setAttribute('aria-label', this.container.dataset.closeLabel);
    close.textContent = '×';
    panel.append(close);

    const content = document.createElement('div');
    content.className = 'tpm-product-looks__popover__content';
    const sourceImage = block.querySelector('.grid-product img');
    if (sourceImage) {
      const image = document.createElement('img');
      image.className = 'tpm-product-looks__popover__image';
      image.src = sourceImage.getAttribute('src');
      image.srcset = sourceImage.getAttribute('srcset') || '';
      image.sizes = '100px';
      image.width = 100;
      image.height = 100;
      image.alt = '';
      image.loading = 'lazy';
      content.append(image);
    } else {
      content.classList.add('tpm-product-looks__popover__content--no-image');
    }

    const title = document.createElement('h3');
    title.id = `${id}-Title`;
    title.className = 'tpm-product-looks__popover__title';
    title.textContent = button.getAttribute('aria-label');
    content.append(title);

    // Preserve the source card's currency, sale price and "from" formatting.
    const price = block.querySelector('.grid-product__price')?.cloneNode(true);
    if (price) {
      price.classList.add('tpm-product-looks__popover__price');
      content.append(price);
    }
    const stock = document.createElement('p');
    stock.className = 'tpm-product-looks__popover__stock';
    stock.textContent = this.container.dataset.stockLabel;
    content.append(stock);

    const link = document.createElement('a');
    link.className = 'btn tpm-product-looks__popover__link';
    link.href = button.dataset.hotspotProductUrl;
    link.textContent = this.container.dataset.viewProductLabel;
    content.append(link);
    panel.append(content);
    this.bindOverlay(panel, this.controller.signal);
    return panel;
  }

  bindOverlay(panel, signal) {
    panel.querySelector('.tpm-product-looks__popover__close').addEventListener('click', () => this.closeOverlay(), {signal});
    // Interacting or scrolling inside the card must not drag the gallery.
    ['pointerdown', 'mousedown', 'touchstart', 'click', 'pswpTap'].forEach(type => {
      panel.addEventListener(type, event => event.stopPropagation(), {
        signal, passive: true
      });
    });
    panel.addEventListener('keydown', event => {
      if (event.key.startsWith('Arrow')) event.stopPropagation();
    }, {signal});
  }

  bindHotspot(button, panel, signal) {
    button.type = 'button';
    button.dataset.tpmLookZoomHotspot = '';
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', panel.id);
    ['pointerdown', 'mousedown', 'touchstart', 'pswpTap'].forEach(type => {
      button.addEventListener(type, event => event.stopPropagation(), {signal, passive: true});
    });
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      if (this.activeOverlay?.button === button) {
        this.closeOverlay();
        return;
      }
      this.closeOverlay(false);
      this.activeOverlay = {button, panel};
      button.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.focus({preventScroll: true});
    }, {signal});
  }

  renderZoomHotspots(gallery, viewer) {
    this.clearZoomHotspots();
    const item = gallery.currItem;
    const slide = item?.el.closest('[data-tpm-look-slide]');
    if (!slide || this.controller.signal.aborted) return;

    this.zoomController = new AbortController();
    const layer = this.zoomLayer = document.createElement('div');
    layer.className = `tpm-product-looks__zoom ${slide.dataset.lookSection}`;
    layer.hidden = true;
    const viewport = viewer.querySelector('.pswp__scroll-wrap');
    viewport.append(layer);

    const photo = slide.querySelector('.hotspots');
    // The source uses a centered cover crop. Convert hotspot positions to the
    // uncropped image coordinates shown by PhotoSwipe (including non-square images).
    const coverScale = Math.max(photo.clientWidth / item.w, photo.clientHeight / item.h);
    const cropX = (item.w - photo.clientWidth / coverScale) / 2;
    const cropY = (item.h - photo.clientHeight / coverScale) / 2;
    const hotspots = [...slide.querySelectorAll('[data-tpm-look-zoom-hotspot]')].map(sourceButton => {
      const style = getComputedStyle(sourceButton);
      const button = sourceButton.cloneNode(true);
      const sourcePanel = document.getElementById(sourceButton.getAttribute('aria-controls'));
      const panel = sourcePanel.cloneNode(true);
      panel.id += '-Zoom';
      panel.hidden = true;
      const title = panel.querySelector('.tpm-product-looks__popover__title');
      title.id = `${panel.id}-Title`;
      panel.setAttribute('aria-labelledby', title.id);
      this.bindOverlay(panel, this.zoomController.signal);
      this.bindHotspot(button, panel, this.zoomController.signal);
      layer.append(button, panel);
      return {
        button,
        x: (parseFloat(style.left) / coverScale + cropX) / item.w,
        y: (parseFloat(style.top) / coverScale + cropY) / item.h
      };
    });

    let previousBounds;
    const positionHotspots = () => {
      // Reading the displayed image rect follows pinch, pan and zoom animation
      // without scaling the controls or product card along with the image.
      const image = item.container?.querySelector('.pswp__img:not(.pswp__img--placeholder)') ||
        item.container?.querySelector('.pswp__img');
      if (image) {
        const rect = image.getBoundingClientRect();
        const frame = viewport.getBoundingClientRect();
        const bounds = [rect.left, rect.top, rect.width, rect.height, frame.width, frame.height].join(',');
        if (bounds !== previousBounds) {
          hotspots.forEach(({button, x, y}) => {
            const left = rect.left - frame.left + x * rect.width;
            const top = rect.top - frame.top + y * rect.height;
            button.style.left = `${left}px`;
            button.style.top = `${top}px`;
            button.hidden = left < 0 || left > frame.width || top < 0 || top > frame.height;
          });
          layer.hidden = !rect.width || !rect.height;
          previousBounds = bounds;
        }
      }
      this.zoomFrame = requestAnimationFrame(positionHotspots);
    };
    positionHotspots();
  }

  clearZoomHotspots() {
    if (this.activeOverlay && this.zoomLayer?.contains(this.activeOverlay.panel)) this.closeOverlay(false);
    if (this.zoomFrame) cancelAnimationFrame(this.zoomFrame);
    if (this.zoomController) this.zoomController.abort();
    if (this.zoomLayer) this.zoomLayer.remove();
    this.zoomLayer = null;
    this.zoomFrame = null;
  }

  closeOverlay(restoreFocus = true) {
    if (!this.activeOverlay) return;
    const {button, panel} = this.activeOverlay;
    this.activeOverlay = null;
    panel.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    if (restoreFocus && button.isConnected) button.focus({preventScroll: true});
  }

  createThumbnail(look, index) {
    const thumb = document.createElement('div');
    thumb.className = 'product__thumb-item';
    thumb.dataset.index = index;
    thumb.dataset.tpmLookThumb = '';
    const link = document.createElement('a');
    link.className = 'product__thumb';
    link.dataset.productThumb = '';
    link.dataset.index = index;
    link.href = look.dataset.lookImage;
    const image = document.createElement('img');
    const url = new URL(look.dataset.lookImage, window.location.origin);
    url.searchParams.set('width', '160');
    image.src = url.href;
    image.width = 80;
    image.height = 80;
    image.loading = 'lazy';
    image.alt = `${this.container.dataset.lookLabel}: ${look.dataset.lookTitle}`;
    link.append(image);
    thumb.append(link);
    return thumb;
  }

  destroy() {
    const product = this.product;
    // Releasing state happens in the finally block, so a throw from Impulse's own
    // slide-change handler cannot leave this element half torn down.
    try {
      this.closeLookTooltip();
      this.closeOverlay(false);
      this.clearZoomHotspots();
      this.galleryEmitter?.off('change', this.onGalleryChange);

      // Hand the gallery methods back so a re-rendered section starts from Impulse's.
      if (product && this.vendor) {
        Object.entries(this.vendor).forEach(([name, method]) => { product[name] = method; });
      }

      // Take back everything this element added outside itself. Without this an editor
      // re-render leaves the old look slides in the gallery while the replacement
      // element appends its own, and a reconnect would duplicate them again.
      const slides = [...(product?.cache?.mainSlider?.querySelectorAll(TPM_LOOK_SLIDE) || [])];
      if (slides.length) {
        const slideshow = product.flickity?.slideshow;
        if (slideshow) {
          // Removing the selected cell makes Flickity select a neighbour, and
          // Impulse's onSlideChange dereferences the outgoing slide without a null
          // check, so move to a surviving slide before any look cell is removed.
          // Choose it from Flickity's own cells: with an image set active the other
          // groups are still in the DOM but are not cells, and selecting one of those
          // is ignored, which leaves Impulse's index stale for later navigation.
          const cells = typeof slideshow.getCellElements === 'function'
            ? slideshow.getCellElements()
            : [...product.cache.mainSlider.querySelectorAll('.product-main-slide')];
          const native = [...cells].find(cell => !cell.matches(TPM_LOOK_SLIDE));
          if (native) slideshow.selectCell(native, false, true);
          slideshow.remove(slides);
        } else {
          slides.forEach(slide => slide.remove());
        }
      }
      product?.cache?.thumbSlider?.querySelectorAll('[data-tpm-look-thumb]')
        .forEach(thumb => thumb.remove());
      this.querySelector('[data-tpm-look-panels]')?.replaceChildren();
    } finally {
      this.galleryEmitter = null;
      this.vendor = null;
      this.hidden = true;
      // Releasing the instance lets connectedCallback run setup again if this element
      // is moved and re-inserted rather than replaced.
      this.product = null;
      this.controller?.abort();
    }
  }
}

// MARK:- Expose Impulse's PhotoSwipe instance and an open event. theme.Photoswipe
// keeps its instance in a local variable and emits nothing when a gallery opens, so
// both are surfaced by wrapping the vendor prototype here rather than in theme.js.
const tpmPatchPhotoswipe = () => {
  const photoswipe = window.theme?.Photoswipe?.prototype;
  if (!photoswipe || photoswipe.tpmPatched) return;
  photoswipe.tpmPatched = true;

  const originalInit = photoswipe.init;
  photoswipe.init = function() {
    // init runs again after look slides are appended; clear the old click bindings
    // first so the zoom triggers are not bound twice.
    this.container.querySelectorAll('.js-photoswipe__zoom').forEach(trigger => {
      if (typeof trigger.off === 'function') trigger.off(`click${this.namespace}`);
    });
    this.container.tpmPhotoswipe = this;
    originalInit.call(this);
  };

  const originalInitGallery = photoswipe.initGallery;
  photoswipe.initGallery = function(items, index) {
    originalInitGallery.call(this, items, index);
    this.container.dispatchEvent(new CustomEvent('tpm:photoswipe:open', {
      bubbles: true,
      detail: {gallery: this.gallery, viewer: document.querySelectorAll('.pswp')[0]}
    }));
  };
};

// MARK:- The element owns its own lifecycle, so the editor's section swap tears the
// old one down and builds the new one without any shopify:section:* bookkeeping.
// Patch before defining, so the first upgrade already has the wrapped initializer.
tpmPatchPhotoswipe();
if (!customElements.get('tpm-product-looks')) {
  customElements.define('tpm-product-looks', TpmProductLooks);
}
